import React, { useEffect, useState } from "react";
import { Container, Table, Modal, Button } from "react-bootstrap";
import PaginationComponent from "../component/Pagination";
import Loader from "../component/Loader";
import Swal from "sweetalert2";
import Select from "react-select";
import { useData, US_STATES } from "../context/DataContext";

import LogoSvg from "../Assets/images/logo.svg";
// import styles from "../Assets/css/electricvehical.module.css";
import styles from "../Assets/css/fleet.module.css";
import "../Assets/css/Hoymiles.css";

const HoymilesDetails = () => {
  const { utilities: contextUtilities, fetchAllData } = useData();
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const HOYMILES_URL = BASE_URL + "devices/unified-list/detail/?type=Hoymiles";

  const [vehicleData, setVehicleData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Utility and State Filters
  const [selectedUtility, setSelectedUtility] = useState("All");
  const [selectedState, setSelectedState] = useState("All");

  const [loading, setLoading] = useState({
    global: false,
    filter: false,
  });

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  
  // Parameter states
  const [inverterSN, setInverterSN] = useState("");
  const [microInverterSN, setMicroInverterSN] = useState("");
  const [activePower, setActivePower] = useState("");
  const [reactivePower, setReactivePower] = useState("");
  const [powerFactor, setPowerFactor] = useState("");
  const [batteryMode, setBatteryMode] = useState("");
  const [reserveSoc, setReserveSoc] = useState("");

  const postsPerPage = 10;

  // Calculate yesterday's date
  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  // ------------------------------
  // DATE + TIME FILTER STATES
  // ------------------------------
  const [startDate, setStartDate] = useState(getYesterdayDate());
  const [startTime, setStartTime] = useState("00:00");

  const [endDate, setEndDate] = useState(getYesterdayDate());
  const [endTime, setEndTime] = useState("23:00");

  const [dateError, setDateError] = useState("");

  // ------------------------------
  // FETCH HOYMILES DATA
  // ------------------------------
  const fetchHoymilesData = async (start = "", end = "", utility = "All", state = "All") => {
    try {
      setLoading((prev) => ({ ...prev, global: true }));

      const storedAccessToken = localStorage.getItem("accessToken");
      if (!storedAccessToken) throw new Error("Access token missing");

      let apiURL = HOYMILES_URL;
      const params = [];
      
      if (start && end) {
        params.push(`startdate=${start}`);
        params.push(`enddate=${end}`);
      }
      if (utility && utility !== "All") {
        params.push(`utility=${encodeURIComponent(utility)}`);
      }
      if (state && state !== "All") {
        params.push(`state=${encodeURIComponent(state)}`);
      }
      
      if (params.length > 0) {
        apiURL = `${HOYMILES_URL}&${params.join('&')}`;
      }

      const response = await fetch(apiURL, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${storedAccessToken}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch data");

      const data = await response.json();
      setVehicleData(data.results || []);
      setFilteredData(data.results || []);
    } catch (error) {
      console.error("HoyMiles Fetch Error:", error);
      setVehicleData([]);
      setFilteredData([]);
    } finally {
      setLoading((prev) => ({ ...prev, global: false }));
    }
  };

  // ------------------------------
  // INITIAL LOAD
  // ------------------------------
  useEffect(() => {
    fetchHoymilesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch utilities if not already loaded in context
  useEffect(() => {
    const storedAccessToken = localStorage.getItem('accessToken');
    if (storedAccessToken && !contextUtilities) {
      fetchAllData(storedAccessToken);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------
  // APPLY FILTER
  // ------------------------------
  const handleFilter = async () => {
    if (!startDate || !endDate) {
      setDateError("Please select Start Date and End Date and Hour");
      return;
    }

    setDateError("");

    const startValue = `${startDate.replace(/-/g, '/')}T${startTime || "00:00"}:00`;
    const endValue = `${endDate.replace(/-/g, '/')}T${endTime || "23:00"}:00`;

    setLoading((prev) => ({ ...prev, filter: true }));

    await fetchHoymilesData(startValue, endValue, selectedUtility, selectedState);
    setCurrentPage(1);

    setLoading((prev) => ({ ...prev, filter: false }));
  };

  // ------------------------------
  // CLEAR FILTER
  // ------------------------------
  const clearFilter = () => {
    setStartDate("");
    setEndDate("");
    setStartTime("");
    setEndTime("");
    setSelectedUtility("All");
    setSelectedState("All");
    setDateError("");

    fetchHoymilesData();
  };

  // ------------------------------
  // OPEN COMMANDS MODAL
  // ------------------------------
  const handleOpenModal = (device) => {
    setSelectedDevice(device);
    setShowModal(true);
    
    // Set inverter SN (first device's SN or empty)
    if (device.devices && device.devices.length > 0) {
      setInverterSN(device.devices[0].sn || "");
      
      // Set micro inverter SN (first subset's SN or empty)
      if (device.devices[0].subsets && device.devices[0].subsets.length > 0) {
        setMicroInverterSN(device.devices[0].subsets[0].sn || "");
      } else {
        setMicroInverterSN("");
      }
    } else {
      setInverterSN("");
      setMicroInverterSN("");
    }
    
    // Reset other fields
    setActivePower("");
    setReactivePower("");
    setPowerFactor("");
    setBatteryMode("");
    setReserveSoc("");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDevice(null);
    // Clear all parameter inputs
    setInverterSN("");
    setMicroInverterSN("");
    setActivePower("");
    setReactivePower("");
    setPowerFactor("");
    setBatteryMode("");
    setReserveSoc("");
  };

  // ------------------------------
  // SEND COMMAND API
  // ------------------------------
  const handleCommand = async (commandSlug, customParams = null) => {
    if (!selectedDevice) return;

    try {
      const storedAccessToken = localStorage.getItem("accessToken");
      if (!storedAccessToken) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Access token missing",
        });
        return;
      }

      // Prepare params based on command type
      let params = {};
      
      if (customParams) {
        // Use custom params if provided
        params = customParams;
      } else {
        // Auto-assign params based on command slug
        if (commandSlug.includes("micro")) {
          if (!microInverterSN) {
            Swal.fire({
              icon: "warning",
              title: "Required Field",
              text: "Please select Micro Inverter SN",
            });
            return;
          }
          params.sn = parseInt(microInverterSN);
        } else if (commandSlug.includes("inverter")) {
          if (!inverterSN) {
            Swal.fire({
              icon: "warning",
              title: "Required Field",
              text: "Please select Inverter SN",
            });
            return;
          }
          params.sn = parseInt(inverterSN);
        }
      }

      // Prepare payload with device_id, device_type, and slug
      const payload = {
        device_id: selectedDevice.station_id,
        device_type: "hoymiles",
        slug: commandSlug,
        ...((commandSlug.includes("plant-power") || commandSlug.includes("battery")) && {
          plantId: selectedDevice.station_id,
        }),
        ...params,
      };

      const response = await fetch(BASE_URL + "devices/send-command/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${storedAccessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to send command");
      }

      const data = await response.json();
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Command sent successfully!",
        timer: 2000,
      });
    } catch (error) {
      console.error("Command Error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to send command. Please try again.",
      });
    }
  };

  // Handle power control commands
  const handlePowerCommand = async (commandSlug, paramName, paramValue) => {
    if (!paramValue) {
      Swal.fire({
        icon: "warning",
        title: "Required Field",
        text: `Please enter ${paramName} value`,
      });
      return;
    }
    
    const params = {};
    params[paramName] = paramValue;
    await handleCommand(commandSlug, params);
  };

  const handleBatteryConfig = async () => {
    if (batteryMode === "" || reserveSoc === "") {
      Swal.fire({
        icon: "warning",
        title: "Required Fields",
        text: "Please enter Mode and Reserve SOC",
      });
      return;
    }

    const modeValue = Number(batteryMode);
    const reserveSocValue = Number(reserveSoc);

    if (Number.isNaN(modeValue) || Number.isNaN(reserveSocValue)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Input",
        text: "Mode and Reserve SOC must be numeric values",
      });
      return;
    }

    await handleCommand("battery-cfg", {
      mode: modeValue,
      reserve_soc: reserveSocValue,
    });
  };

  // ------------------------------
  // PAGINATION
  // ------------------------------
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredData.slice(indexOfFirstPost, indexOfLastPost);

  return (
    <div className={styles.fleet_view_cantaint}>
      {loading.global && <Loader />}

      {/* HEADER */}
      <section className={styles.topbar}>
        <div className={styles.container}>
          <img className={styles.topbar_logo} src={LogoSvg} alt="Topbar Logo" />
        </div>
      </section>

      <section className="thermostat-details electric-vehicle-details">
        <Container>
          <h2 className={styles.main_title}>HoyMiles Details</h2>

          {/* -------------------- FILTER UI -------------------- */}
          <div className="row g-2 mt-3 mb-2 align-items-end">
            {/* Start Date */}
            <div className="col-6 col-md-3">
              <label className="small">Start Date</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            {/* Start Time */}
            <div className="col-6 col-md-2">
              <label className="small">Start Hour</label>
              <select
                className="form-control form-control-sm"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              >
                <option value="">Hr</option>
                {Array.from({ length: 24 }).map((_, i) => {
                  const hour = i.toString().padStart(2, "0");
                  return (
                    <option key={i} value={`${hour}:00`}>
                      {hour}:00
                    </option>
                  );
                })}
              </select>
            </div>

            {/* End Date */}
            <div className="col-6 col-md-3">
              <label className="small">End Date</label>
              <input
                type="date"
                min={startDate}
                className="form-control form-control-sm"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            {/* End Time */}
            <div className="col-6 col-md-2">
              <label className="small">End Hour</label>
              <select
                className="form-control form-control-sm"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              >
                <option value="">Hr</option>
                {Array.from({ length: 24 }).map((_, i) => {
                  const hour = i.toString().padStart(2, "0");
                  return (
                    <option key={i} value={`${hour}:00`}>
                      {hour}:00
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Utility Filter */}
            {/* <div className="col-6 col-md-4">
              <label className="small">Utility</label>
              <Select
                options={[
                  { value: "All", label: "All Utility" },
                  ...(contextUtilities || []).map((utility) => ({
                    value: utility.id.toString(),
                    label: utility.utility_name,
                  })),
                ]}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    backgroundColor: "#fff",
                    borderColor: "#b6b6b6",
                    minHeight: "31px",
                    height: "31px",
                    boxShadow: "none",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#4c2806",
                    borderRadius: "3px",
                    cursor: "pointer"
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: "31px",
                    padding: "0 6px",
                    display: "flex",
                    alignItems: "center"
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    color: "#4c2806",
                    position: "relative",
                    top: "auto",
                    transform: "none",
                    margin: 0,
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: "0px",
                    padding: "0px"
                  }),
                  indicatorSeparator: () => ({
                    display: "none",
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: "31px",
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                    fontSize: "13px",
                    color: "#4c2806"
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? "#ffe0b2" : state.isFocused ? "#fff7e9" : "white",
                    color: "#4c2806",
                    cursor: "pointer"
                  })
                }}
                value={
                  selectedUtility === "All"
                    ? { value: "All", label: "All Utility" }
                    : (contextUtilities || []).map(u => ({ value: u.id.toString(), label: u.utility_name })).find(opt => opt.value === selectedUtility)
                }
                onChange={(option) => setSelectedUtility(option ? option.value : "All")}
                placeholder="Select Utility"
                isSearchable
                isClearable
              />
            </div> */}

            {/* State Filter */}
            {/* <div className="col-6 col-md-4">
              <label className="small">State</label>
              <Select
                options={[
                  { value: "All", label: "All States" },
                  ...US_STATES.map((state) => ({ value: state, label: state })),
                ]}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    backgroundColor: "#fff",
                    borderColor: "#b6b6b6",
                    minHeight: "31px",
                    height: "31px",
                    boxShadow: "none",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "#4c2806",
                    borderRadius: "3px",
                    cursor: "pointer"
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: "31px",
                    padding: "0 6px",
                    display: "flex",
                    alignItems: "center"
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    color: "#4c2806",
                    position: "relative",
                    top: "auto",
                    transform: "none",
                    margin: 0,
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: "0px",
                    padding: "0px"
                  }),
                  indicatorSeparator: () => ({
                    display: "none",
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: "31px",
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                    fontSize: "13px",
                    color: "#4c2806"
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? "#ffe0b2" : state.isFocused ? "#fff7e9" : "white",
                    color: "#4c2806",
                    cursor: "pointer"
                  })
                }}
                value={
                  selectedState === "All"
                    ? { value: "All", label: "All States" }
                    : { value: selectedState, label: selectedState }
                }
                onChange={(option) => setSelectedState(option ? option.value : "All")}
                placeholder="Select State"
                isSearchable
                isClearable
              />
            </div> */}

            {/* Buttons */}
            <div className="col-12 col-md-2 d-flex gap-2 mt-md-0 mt-2">
              <button
                className="btn btn-sm filter-button"
                style={{ backgroundColor: "#ff5900", color: "white", border: "none" }}
                onClick={handleFilter}
              >
                Filter
              </button>

              <button
                className="btn btn-secondary btn-sm filter-button"
                onClick={clearFilter}
              >
                Clear
              </button>
            </div>
          </div>

          {dateError && <div style={{ color: "red" }}>{dateError}</div>}

          {/* -------------------- TABLE -------------------- */}
          <div className="table-responsive mt-4">
            <Table className={styles.table}>
              <thead>
                <tr>
                  <th>Device ID</th>
                  <th className="text-center">Station Name</th>
                  <th className="text-center">Inverter SN</th>
                  <th className="text-center">Micro Inverter SN</th>
                  <th className="text-center">Production (kWh)</th>
                  <th className="text-center">Consumption (kWh)</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {currentPosts.length > 0 ? (
                  currentPosts.map((device, index) => {
                    // Extract Inverter SNs from devices array
                    const inverterSNs = device.devices && device.devices.length > 0
                      ? device.devices.map(d => d.sn).join(", ")
                      : "-";
                    
                    // Extract Micro Inverter SNs from all subsets
                    const microInverterSNs = device.devices && device.devices.length > 0
                      ? device.devices
                          .filter(d => d.subsets && d.subsets.length > 0)
                          .flatMap(d => d.subsets.map(s => s.sn))
                          .join(", ")
                      : "-";
                    
                    return (
                      <tr key={index}>
                        <td>{device.station_id}</td>
                        <td className="text-center">{device.station_name}</td>
                        <td className="text-center">{inverterSNs}</td>
                        <td className="text-center">{microInverterSNs || "-"}</td>
                        <td className="text-center">{device.total_production}</td>
                        <td className="text-center">{device.total_consumption}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm"
                            style={{ backgroundColor: "#ff5900", color: "white", border: "none" }}
                            onClick={() => handleOpenModal(device)}
                          >
                            Commands
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <PaginationComponent
            currentPage={currentPage}
            totalPages={Math.ceil(filteredData.length / postsPerPage)}
            onPageChange={setCurrentPage}
          />
        </Container>
      </section>

      {/* -------------------- COMMANDS MODAL -------------------- */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Device Commands</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDevice && (
            <div>
              <p>
                <strong>Station Name:</strong> {selectedDevice.station_name}
              </p>
              <p>
                <strong>Plant ID:</strong> {selectedDevice.station_id}
              </p>
              
              <h6 className="mt-4 mb-3">Microinverter Controls</h6>
              <div className="mb-3">
                <label className="form-label">
                  Micro Inverter SN <span style={{ color: "red" }}>*</span>
                </label>
                {selectedDevice.devices && selectedDevice.devices.length > 0 && 
                 selectedDevice.devices[0].subsets && selectedDevice.devices[0].subsets.length > 1 ? (
                  <select
                    className="form-control"
                    value={microInverterSN}
                    onChange={(e) => setMicroInverterSN(e.target.value)}
                  >
                    {selectedDevice.devices[0].subsets.map((subset, idx) => (
                      <option key={idx} value={subset.sn}>
                        {subset.sn}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    value={microInverterSN}
                    readOnly
                  />
                )}
              </div>
              <div className="row g-2">
                <div className="col-4">
                  <Button
                    variant="success"
                    className="w-100"
                    onClick={() => handleCommand("micro-on")}
                  >
                    ON Micro
                  </Button>
                </div>
                <div className="col-4">
                  <Button
                    variant="danger"
                    className="w-100"
                    onClick={() => handleCommand("micro-off")}
                  >
                    OFF Micro
                  </Button>
                </div>
                <div className="col-4">
                  <Button
                    variant="warning"
                    className="w-100"
                    onClick={() => handleCommand("micro-restart")}
                  >
                    Restart Micro
                  </Button>
                </div>
              </div>

              <h6 className="mt-4 mb-3">Inverter Controls</h6>
              <div className="mb-3">
                <label className="form-label">
                  Inverter SN <span style={{ color: "red" }}>*</span>
                </label>
                {selectedDevice.devices && selectedDevice.devices.length > 1 ? (
                  <select
                    className="form-control"
                    value={inverterSN}
                    onChange={(e) => setInverterSN(e.target.value)}
                  >
                    {selectedDevice.devices.map((device, idx) => (
                      <option key={idx} value={device.sn}>
                        {device.sn}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-control"
                    value={inverterSN}
                    readOnly
                  />
                )}
              </div>
              <div className="row g-2">
                <div className="col-4">
                  <Button
                    variant="success"
                    className="w-100"
                    onClick={() => handleCommand("inverter-on")}
                  >
                    ON Inverter
                  </Button>
                </div>
                <div className="col-4">
                  <Button
                    variant="danger"
                    className="w-100"
                    onClick={() => handleCommand("inverter-off")}
                  >
                    OFF Inverter
                  </Button>
                </div>
                <div className="col-4">
                  <Button
                    variant="warning"
                    className="w-100"
                    onClick={() => handleCommand("inverter-restart")}
                  >
                    Restart Inverter
                  </Button>
                </div>
              </div>

              <h6 className="mt-4 mb-3">Power Controls</h6>
              <div className="row g-2 mb-3">
                <div className="col-4">
                  <label className="form-label small">
                    Active Power (p_a) <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={activePower}
                    onChange={(e) => setActivePower(e.target.value)}
                    placeholder="Value"
                  />
                </div>
                <div className="col-4">
                  <label className="form-label small">
                    Reactive Power (p_a) <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={reactivePower}
                    onChange={(e) => setReactivePower(e.target.value)}
                    placeholder="Value"
                  />
                </div>
                <div className="col-4">
                  <label className="form-label small">
                    Power Factor (pf_a) <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={powerFactor}
                    onChange={(e) => setPowerFactor(e.target.value)}
                    placeholder="Value"
                  />
                </div>
              </div>
              <div className="row g-2">
                <div className="col-4">
                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={() => handlePowerCommand("plant-power-ctl-a", "p_a", activePower)}
                  >
                    Set Active Power
                  </Button>
                </div>
                <div className="col-4">
                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={() => handlePowerCommand("plant-power-ctl-r", "p_a", reactivePower)}
                  >
                    Set Reactive Power
                  </Button>
                </div>
                <div className="col-4">
                  <Button
                    variant="primary"
                    className="w-100"
                    onClick={() => handlePowerCommand("plant-power-ctl-f", "pf_a", powerFactor)}
                  >
                    Set Power Factor
                  </Button>
                </div>
              </div>

              <h6 className="mt-4 mb-3">Battery Config</h6>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label small">
                    Mode <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={batteryMode}
                    onChange={(e) => setBatteryMode(e.target.value)}
                    placeholder="e.g. 1"
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small">
                    Reserve SOC <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={reserveSoc}
                    onChange={(e) => setReserveSoc(e.target.value)}
                    placeholder="e.g. 100"
                    min="0"
                    max="100"
                  />
                </div>
              </div>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <Button variant="primary" className="w-100" onClick={handleBatteryConfig}>
                    Send Battery Config
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default HoymilesDetails;
